import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, CircularProgress, IconButton, InputBase, MenuItem, Select, TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UnsubscribeOutlinedIcon from "@mui/icons-material/UnsubscribeOutlined";

import { CustomBtn } from "../../../components/Common/CustomBtn";
import { colors } from "../../../theme/theme";
import { useSubscribers } from "./NewsletterSubscribers.hooks";
import {
  RootBox, HeaderRow, PageHeading, PageSubheading, StatsRow, StatCard, StatValue, StatLabel,
  ToolbarCard, TableCard, HeaderCell, RowGrid, EmailText, CellText, StatusPill,
  LoadingBox, EmptyBox,
} from "./NewsletterSubscribers.styles";

const fmtDate = (d) => {
  if (!d) return "—";
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toISOString().slice(0, 10);
};

const STATUS_ICON = {
  active: <CheckCircleIcon sx={{ fontSize: 15 }} />,
  unsubscribed: <UnsubscribeOutlinedIcon sx={{ fontSize: 15 }} />,
};

// Quoted on every field: addresses are fine but the source column is free text,
// and Excel splits on a stray comma otherwise.
const toCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["email", "status", "subscribed", "unsubscribed", "source"];
  const lines = rows.map((r) =>
    [
      r.email,
      r.unsubscribedAt ? "unsubscribed" : "active",
      fmtDate(r.createdAt),
      r.unsubscribedAt ? fmtDate(r.unsubscribedAt) : "",
      r.source || "",
    ].map(esc).join(",")
  );
  return [head.map(esc).join(","), ...lines].join("\n");
};

const NewsletterSubscribers = () => {
  const { items, loading, error, reload } = useSubscribers();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const stats = useMemo(() => {
    const active = items.filter((i) => !i.unsubscribedAt).length;
    return { total: items.length, active, unsubscribed: items.length - active };
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      const status = i.unsubscribedAt ? "unsubscribed" : "active";
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (q && !`${i.email || ""} ${i.source || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, statusFilter]);

  // Any change to the result set can leave the current page empty.
  useEffect(() => { setPage(0); }, [search, statusFilter, items.length]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Exports what is on screen, not the whole table — the filter is the selection.
  const exportCsv = () => {
    const blob = new Blob([toCsv(filtered)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nirva-blog-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const STAT_CARDS = [
    { key: "all", label: "Total", value: stats.total, color: colors.primary },
    { key: "active", label: "Active", value: stats.active, color: "#409d45" },
    { key: "unsubscribed", label: "Unsubscribed", value: stats.unsubscribed, color: colors.textLight },
  ];

  return (
    <RootBox>
      <HeaderRow>
        <div>
          <PageHeading>Newsletter Subscribers</PageHeading>
          <PageSubheading>People signed up for new blog articles</PageSubheading>
        </div>
        <CustomBtn
          text="Export CSV"
          Icon={FileDownloadOutlinedIcon}
          onClick={exportCsv}
          disabled={!filtered.length}
          noMb
          noMt
        />
      </HeaderRow>

      {/* Cards double as the status filter, same as the Practitioners screen. */}
      <StatsRow>
        {STAT_CARDS.map((c) => (
          <StatCard key={c.key} $active={statusFilter === c.key} onClick={() => setStatusFilter(c.key)}>
            <StatValue $color={c.color}>{c.value}</StatValue>
            <StatLabel>{c.label}</StatLabel>
          </StatCard>
        ))}
      </StatsRow>

      <ToolbarCard>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 240 }}>
          <SearchIcon sx={{ color: colors.textLight }} />
          <InputBase
            placeholder="Search by email or source..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, fontFamily: "'Quicksand', sans-serif" }}
          />
          {search && (
            <IconButton size="small" onClick={() => setSearch("")} aria-label="Clear search">
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </div>
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 160, fontFamily: "'Quicksand', sans-serif", borderRadius: 2 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
        </Select>
      </ToolbarCard>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<CustomBtn text="Retry" onClick={reload} noMb noMt />}>
          {error}
        </Alert>
      )}

      <TableCard>
        <RowGrid sx={{ borderBottom: `1px solid ${colors.headerBg}` }}>
          <HeaderCell>Email</HeaderCell>
          <HeaderCell>Status</HeaderCell>
          <HeaderCell>Subscribed</HeaderCell>
          <HeaderCell>Unsubscribed</HeaderCell>
          <HeaderCell>Source</HeaderCell>
        </RowGrid>

        {loading ? (
          <LoadingBox>
            <CircularProgress sx={{ color: colors.secondary }} />
          </LoadingBox>
        ) : filtered.length === 0 ? (
          <EmptyBox>{items.length ? "No subscribers match this filter." : "No subscribers yet."}</EmptyBox>
        ) : (
          paged.map((s) => {
            const status = s.unsubscribedAt ? "unsubscribed" : "active";
            return (
              <RowGrid key={s.id || s.email}>
                <EmailText title={s.email}>{s.email}</EmailText>
                <StatusPill $status={status}>
                  {STATUS_ICON[status]}
                  {status}
                </StatusPill>
                <CellText>{fmtDate(s.createdAt)}</CellText>
                <CellText>{s.unsubscribedAt ? fmtDate(s.unsubscribedAt) : "—"}</CellText>
                <CellText>{s.source || "—"}</CellText>
              </RowGrid>
            );
          })
        )}

        {!loading && filtered.length > 0 && (
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            sx={{
              borderTop: `1px solid ${colors.headerBg}`,
              ".MuiTablePagination-toolbar, .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows, .MuiTablePagination-select":
                { fontFamily: "'Quicksand', sans-serif" },
            }}
          />
        )}
      </TableCard>
    </RootBox>
  );
};

export default NewsletterSubscribers;
